import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FileSearch } from 'lucide-react';

import { EmptyState } from '../../../components/admin/EmptyState';
import { useAiLogSummary, useAiLogs } from '../../../services/aiLog.service';
import type { AiLogFilters } from '../../../types/aiLog';
import { AiLogDetailDrawer } from './AiLogDetailDrawer';
import { createAiLogsColumns } from './AiLogsColumns';
import { AiLogDatePreset, AiLogsFilterDraft, AiLogsFilters } from './AiLogsFilters';
import { AiLogsSummaryCards } from './AiLogsSummaryCards';

const MAX_RANGE_MS = 31 * 24 * 60 * 60 * 1000;

const toLocalInputValue = (date: Date): string => {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const rangeForPreset = (preset: AiLogDatePreset) => {
  const to = new Date();
  const hours = preset === '1h' ? 1 : preset === '7d' ? 24 * 7 : preset === '31d' ? 24 * 31 : 24;
  const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
  return { from: toLocalInputValue(from), to: toLocalInputValue(to) };
};

const initialDateRange = rangeForPreset('24h');

const initialDraft: AiLogsFilterDraft = {
  preset: '24h',
  from: initialDateRange.from,
  to: initialDateRange.to,
  user: '',
  origin: '',
  model: '',
  capabilityCode: '',
  status: '',
  variance: '',
};

const useDebouncedValue = (value: string, delayMs: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedValue(value), delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [delayMs, value]);

  return debouncedValue;
};

const dateRangeTooLong = (from: string, to: string): boolean => {
  const fromTime = new Date(from).getTime();
  const toTime = new Date(to).getTime();
  if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) return false;
  return toTime - fromTime > MAX_RANGE_MS;
};

const userFilter = (value: string): Pick<AiLogFilters, 'userId' | 'email'> => {
  const trimmed = value.trim();
  if (!trimmed) return {};
  return /^\d+$/.test(trimmed) ? { userId: trimmed } : { email: trimmed };
};

export const AiLogsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [draft, setDraft] = useState<AiLogsFilterDraft>(initialDraft);
  const [committedRange, setCommittedRange] = useState({
    from: initialDraft.from,
    to: initialDraft.to,
  });
  const [selectedPublicId, setSelectedPublicId] = useState<string | null>(null);

  const debouncedUser = useDebouncedValue(draft.user, 400);
  const debouncedModel = useDebouncedValue(draft.model, 400);
  const debouncedCapability = useDebouncedValue(draft.capabilityCode, 400);
  const columns = useMemo(() => createAiLogsColumns(), []);

  const filters = useMemo<AiLogFilters>(
    () => ({
      from: committedRange.from,
      to: committedRange.to,
      ...userFilter(debouncedUser),
      origin: draft.origin,
      model: debouncedModel,
      capabilityCode: debouncedCapability,
      status: draft.status,
      variance: draft.variance,
    }),
    [
      committedRange.from,
      committedRange.to,
      debouncedCapability,
      debouncedModel,
      debouncedUser,
      draft.origin,
      draft.status,
      draft.variance,
    ]
  );

  useEffect(() => {
    setPage(0);
  }, [filters]);

  const {
    data: logList,
    isLoading,
    isFetching,
    error,
    refetch: refetchLogs,
  } = useAiLogs(page + 1, pageSize, filters);
  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    refetch: refetchSummary,
  } = useAiLogSummary(filters);

  const handleDraftChange = <K extends keyof AiLogsFilterDraft>(
    key: K,
    value: AiLogsFilterDraft[K]
  ) => {
    setDraft((current) => ({
      ...current,
      [key]: value,
      preset: key === 'from' || key === 'to' ? 'custom' : current.preset,
    }));
    if (
      key !== 'user' &&
      key !== 'model' &&
      key !== 'capabilityCode' &&
      key !== 'from' &&
      key !== 'to'
    ) {
      setPage(0);
    }
  };

  const handlePresetChange = (preset: AiLogDatePreset) => {
    if (preset === 'custom') {
      setDraft((current) => ({ ...current, preset }));
      return;
    }
    const nextRange = rangeForPreset(preset);
    setDraft((current) => ({ ...current, preset, ...nextRange }));
    setCommittedRange(nextRange);
    setPage(0);
  };

  const handleApplyDateRange = () => {
    if (dateRangeTooLong(draft.from, draft.to)) return;
    setCommittedRange({ from: draft.from, to: draft.to });
    setPage(0);
  };

  const handleReset = () => {
    const nextRange = rangeForPreset('24h');
    setDraft({ ...initialDraft, ...nextRange });
    setCommittedRange(nextRange);
    setPage(0);
  };

  const handleRefresh = () => {
    void refetchLogs();
    void refetchSummary();
  };

  const rows = logList?.items ?? [];
  const meta = logList?.meta;
  const hasVarianceAlert = Boolean(meta?.truncated || meta?.varianceFilteredInMemory);
  const rangeTooLong = dateRangeTooLong(draft.from, draft.to);

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      <Box className="mb-6 flex flex-col gap-2">
        <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
          AI Usage Logs
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
          Monitor model calls, estimated credits, Cloudflare costs, latency, services, and tools.
        </Typography>
      </Box>

      <Box className="mb-4">
        <AiLogsSummaryCards summary={summary} loading={summaryLoading} />
      </Box>

      <Box className="mb-4">
        <AiLogsFilters
          draft={draft}
          dateRangeTooLong={rangeTooLong}
          refreshing={isFetching || summaryFetching}
          onDraftChange={handleDraftChange}
          onPresetChange={handlePresetChange}
          onApplyDateRange={handleApplyDateRange}
          onReset={handleReset}
          onRefresh={handleRefresh}
        />
      </Box>

      {hasVarianceAlert && (
        <Alert severity="info" className="mb-4">
          Variance filter is applied to the latest 2000 matching calls.
        </Alert>
      )}

      <Box className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <Box className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-slate-700">
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Calls
          </Typography>
          {isFetching && !isLoading && <CircularProgress size={20} />}
        </Box>

        {isLoading ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className="p-6">
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetchLogs()}>
                  Retry
                </Button>
              }
            >
              Failed to load AI usage logs.
            </Alert>
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            Icon={FileSearch}
            title="No AI usage logs"
            description="Try a wider date range or clear the filters."
            actionLabel="Reset filters"
            onAction={handleReset}
          />
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.publicId}
            loading={isFetching}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={meta?.total ?? rows.length}
            pageSizeOptions={[10, 20, 50, 100]}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            onRowClick={(params) => setSelectedPublicId(params.row.publicId)}
            autoHeight
            rowHeight={76}
            className="border-0"
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        )}
      </Box>

      <AiLogDetailDrawer
        publicId={selectedPublicId}
        open={Boolean(selectedPublicId)}
        onClose={() => setSelectedPublicId(null)}
      />
    </Box>
  );
};
